// articlesService.js

import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, auth } from '../firebase';

export const articlesService = {
  /**
   * Créer un nouvel article
   */
  async createArticle(articleData, userId) {
    try {
      const article = {
        ...articleData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        published: articleData.published || false,
        views: 0,
      };

      const docRef = await addDoc(collection(db, 'articles'), article);

      return docRef.id;
    } catch (error) {
      console.error('❌ Erreur création article:', error);
      throw error;
    }
  },

  /**
   * Mettre à jour un article
   */
  async updateArticle(articleId, articleData) {
    try {
      const articleRef = doc(db, 'articles', articleId);
      await updateDoc(articleRef, {
        ...articleData,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('❌ Erreur mise à jour:', error);
      throw error;
    }
  },

  /**
   * Supprimer un article
   */
  async deleteArticle(articleId) {
    try {
      // 🔍 Vérification auth
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error("❌ Non authentifié - connectez-vous d'abord");
      }

      const articleRef = doc(db, 'articles', articleId);
      await deleteDoc(articleRef);
    } catch (error) {
      console.error('❌ Erreur suppression:', error);
      throw error;
    }
  },

  /**
   * Récupérer tous les articles (admin)
   */
  async getAllArticles() {
    try {
      const articlesRef = collection(db, 'articles');
      const q = query(articlesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const articles = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return articles;
    } catch (error) {
      console.error('❌ Erreur récupération articles:', error);
      throw error;
    }
  },

  /**
   * Récupérer les articles publiés (public)
   */
  async getPublishedArticles() {
    try {
      const articlesRef = collection(db, 'articles');
      const q = query(articlesRef, where('published', '==', true), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const articles = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return articles;
    } catch (error) {
      console.error('❌ Erreur récupération articles publiés:', error);
      throw error;
    }
  },

  /**
   * Récupérer un article par ID
   */
  async getArticleById(articleId) {
    try {
      const articleRef = doc(db, 'articles', articleId);
      const articleSnap = await getDoc(articleRef);

      if (articleSnap.exists()) {
        return {
          id: articleSnap.id,
          ...articleSnap.data(),
        };
      } else {
        throw new Error('Article non trouvé');
      }
    } catch (error) {
      console.error('❌ Erreur récupération article:', error);
      throw error;
    }
  },

  /**
   * Rechercher des articles
   */
  async searchArticles(searchQuery) {
    try {
      const articlesRef = collection(db, 'articles');
      const q = query(articlesRef, where('published', '==', true));
      const snapshot = await getDocs(q);

      const lowerQuery = searchQuery.toLowerCase();
      const articles = snapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .filter(
          (article) =>
            article.title?.toLowerCase().includes(lowerQuery) ||
            article.description?.toLowerCase().includes(lowerQuery) ||
            article.tags?.some((tag) => tag.toLowerCase().includes(lowerQuery)) ||
            article.category?.toLowerCase().includes(lowerQuery)
        );

      return articles;
    } catch (error) {
      console.error('❌ Erreur recherche:', error);
      throw error;
    }
  },

  /**
   * Upload une image vers Firebase Storage
   */
  async uploadImage(file, articleId) {
    try {
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `articles/${articleId}/${fileName}`);

      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      return downloadURL;
    } catch (error) {
      console.error('❌ Erreur upload image:', error);
      throw error;
    }
  },

  /**
   * Supprimer une image de Firebase Storage
   */
  async deleteImage(imageUrl) {
    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    } catch (error) {
      console.error('❌ Erreur suppression image:', error);
      throw error;
    }
  },
};
